import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { fetchCreditPoolStatus, createCreditTopup } from '../../store/slices/limitsSlice';
import { formatCredits, formatCreditsCurrency, getStatusColor, getStatusBgColor } from '../../utils/limitChecks';
import { 
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';

interface CreditPoolWidgetProps {
  orgId?: number;
  title?: string;
  showTopupButton?: boolean;
  compact?: boolean;
  className?: string;
  onPoolLow?: () => void;
}

interface TopupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (amount: number, reason: string) => void;
  loading: boolean;
}

const TopupModal: React.FC<TopupModalProps> = ({ isOpen, onClose, onSubmit, loading }) => {
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);
    if (numAmount > 0 && reason.trim()) {
      onSubmit(numAmount, reason.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-surface rounded-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Add Credit Top-up
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Amount (Credits)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="1"
              step="0.01"
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter credit amount"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Reason
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter reason for top-up"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !amount || !reason.trim()}
              className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <PlusIcon className="w-4 h-4" />
                  Add Top-up
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreditPoolWidget: React.FC<CreditPoolWidgetProps> = ({
  orgId,
  title = 'Organization Credit Pool',
  showTopupButton = true,
  compact = false,
  className = '',
  onPoolLow,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { creditPools, loading } = useAppSelector((state) => state.limits);
  
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupLoading, setTopupLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const effectiveOrgId = orgId || user?.organization_id;
  const poolData = effectiveOrgId ? creditPools[effectiveOrgId] : null;

  useEffect(() => {
    if (effectiveOrgId) {
      dispatch(fetchCreditPoolStatus(effectiveOrgId));
      setLastUpdated(new Date());
      
      // Set up auto-refresh every 60 seconds
      const interval = setInterval(() => {
        dispatch(fetchCreditPoolStatus(effectiveOrgId));
        setLastUpdated(new Date());
      }, 60000);
      
      return () => clearInterval(interval);
    }
  }, [dispatch, effectiveOrgId]);

  useEffect(() => {
    if (poolData && poolData.usage_percentage > 80 && onPoolLow) {
      onPoolLow();
    }
  }, [poolData, onPoolLow]);

  const handleTopup = async (amount: number, reason: string) => {
    if (!effectiveOrgId) return;
    
    try {
      setTopupLoading(true);
      await dispatch(createCreditTopup({
        orgId: effectiveOrgId,
        topupData: {
          amount,
          topup_type: 'manual',
          reason,
        },
      })).unwrap();
      
      // Refresh pool status
      dispatch(fetchCreditPoolStatus(effectiveOrgId));
      setShowTopupModal(false);
    } catch (error) {
      console.error('Failed to create topup:', error);
    } finally {
      setTopupLoading(false);
    }
  };

  const getPoolStatus = (usagePercentage: number): 'normal' | 'warning' | 'critical' => {
    if (usagePercentage >= 90) return 'critical';
    if (usagePercentage >= 80) return 'warning';
    return 'normal';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'normal':
        return <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'warning':
      case 'critical':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
      default:
        return null;
    }
  };

  if (loading.creditChecks && !poolData) {
    return (
      <div className={`bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6 ${className}`}>
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!poolData) {
    return null;
  }

  const status = getPoolStatus(poolData.usage_percentage);

  if (compact) {
    return (
      <div className={`bg-white dark:bg-dark-surface rounded-lg border border-light-border dark:border-dark-border p-4 ${className}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {getStatusIcon(status)}
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Pool: {formatCredits(poolData.remaining)} / {formatCredits(poolData.total_pool)}
            </span>
          </div>
          {showTopupButton && user?.role === 'super_admin' && (
            <button
              onClick={() => setShowTopupModal(true)}
              className="p-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full transition-all duration-500 ${
              status === 'critical' ? 'bg-red-500' :
              status === 'warning' ? 'bg-yellow-500' :
              'bg-green-500'
            }`}
            style={{ width: `${poolData.usage_percentage}%` }}
          />
        </div>
        
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>{poolData.usage_percentage.toFixed(1)}% used</span>
          <span>{poolData.pool_type}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6 ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <ClockIcon className="w-3 h-3" />
                <span>Updated {lastUpdated.toLocaleTimeString()}</span>
              </div>
            )}
            {showTopupButton && user?.role === 'super_admin' && (
              <button
                onClick={() => setShowTopupModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Top-up
              </button>
            )}
          </div>
        </div>

        {/* Status Alert */}
        <div className={`mb-4 p-3 rounded-lg ${getStatusBgColor(status)}`}>
          <div className="flex items-center gap-2">
            {getStatusIcon(status)}
            <span className={`text-sm font-medium ${getStatusColor(status)}`}>
              {status === 'critical' ? 'Critical: Pool nearly depleted' :
               status === 'warning' ? 'Warning: Pool usage is high' :
               'Pool status is healthy'}
            </span>
          </div>
        </div>

        {/* Pool Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Pool Usage
            </span>
            <span className="text-sm font-medium text-primary-500">
              {poolData.usage_percentage.toFixed(1)}%
            </span>
          </div>
          
          <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`absolute top-0 left-0 h-full transition-all duration-500 ${
                status === 'critical' ? 'bg-red-500' :
                status === 'warning' ? 'bg-yellow-500' :
                'bg-green-500'
              }`}
              style={{ width: `${poolData.usage_percentage}%` }}
            />
          </div>
        </div>

        {/* Pool Details */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {formatCredits(poolData.used)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Used ({formatCreditsCurrency(poolData.used)})
            </div>
          </div>
          
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {formatCredits(poolData.remaining)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Remaining ({formatCreditsCurrency(poolData.remaining)})
            </div>
          </div>
        </div>

        {/* Total Pool */}
        <div className="text-center p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg mb-4">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            Total {poolData.pool_type.charAt(0).toUpperCase() + poolData.pool_type.slice(1)} Pool
          </div>
          <div className="text-xl font-bold text-primary-600 dark:text-primary-400">
            {formatCredits(poolData.total_pool)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            ({formatCreditsCurrency(poolData.total_pool)} USD equivalent)
          </div>
        </div>

        {/* Auto Top-up Status */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <ArrowTrendingUpIcon className="w-4 h-4 text-blue-500" />
            <span className="font-medium text-blue-600 dark:text-blue-400">
              Auto Top-up:
            </span>
            <span className="text-gray-700 dark:text-gray-300">
              {poolData.auto_topup_enabled ? 
                `Enabled (${formatCredits(poolData.auto_topup_amount)} at ${poolData.auto_topup_threshold}%)` :
                'Disabled'
              }
            </span>
          </div>
        </div>
      </div>

      <TopupModal
        isOpen={showTopupModal}
        onClose={() => setShowTopupModal(false)}
        onSubmit={handleTopup}
        loading={topupLoading}
      />
    </>
  );
};

export default CreditPoolWidget;
